<template>
  <div class="mk-page">
    <MarketplaceHeader />

    <section class="container py-4">
      <h1 class="h4 font-weight-bold mb-3"><i class="fe fe-shopping-cart mr-2"></i>Your cart</h1>

      <div v-if="!items.length" class="mk-panel text-center text-muted py-5">
        <i class="fe fe-shopping-cart" style="font-size: 2.5rem; opacity: .3;"></i>
        <p class="mt-2">Your cart is empty.</p>
        <router-link to="/marketplace" class="btn mk-btn">Browse the marketplace</router-link>
      </div>

      <div v-else class="row">
        <div class="col-lg-8 mb-4">
          <div class="mk-panel mb-3" v-for="group in groups" :key="group.vendor.id">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="font-weight-bold mb-0"><i class="fe fe-briefcase mr-1"></i>{{ group.vendor.name || "Vendor" }}</h6>
              <span class="small text-muted">Separate order</span>
            </div>

            <div class="mk-line" v-for="item in group.items" :key="item.product.id">
              <div class="mk-line-img">
                <img v-if="item.product.image" :src="item.product.image" :alt="item.product.name" />
                <i v-else class="fe fe-package"></i>
              </div>
              <div class="flex-fill">
                <router-link :to="`/marketplace/products/${item.product.id}`" class="font-weight-bold">{{ item.product.name }}</router-link>
                <div class="small text-muted">{{ money(item.product.unit_price) }} / {{ item.product.unit }}
                  <span v-if="item.product.min_order_quantity > 1"> · min {{ item.product.min_order_quantity }}</span>
                </div>
              </div>
              <div>
                <label :for="'qty-' + item.product.id" class="sr-only">Quantity of {{ item.product.name }}</label>
                <input :id="'qty-' + item.product.id" type="number" class="form-control form-control-sm" style="width: 90px;"
                  :min="item.product.min_order_quantity" :value="item.quantity" @change="setQty(item, $event.target.value)" />
              </div>
              <div class="mk-line-total">{{ money(item.quantity * item.product.unit_price) }}</div>
              <button type="button" class="btn btn-sm btn-link text-danger" :aria-label="'Remove ' + item.product.name"
                @click="remove(item)">
                <i class="fe fe-trash-2"></i>
              </button>
            </div>
            <div class="text-right font-weight-bold mt-2">Subtotal: {{ money(group.total) }}</div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="mk-panel">
            <h6 class="font-weight-bold">Order summary</h6>
            <div class="d-flex justify-content-between small"><span>Items</span><span>{{ items.length }}</span></div>
            <div class="d-flex justify-content-between small"><span>Vendors (orders)</span><span>{{ groups.length }}</span></div>
            <div class="d-flex justify-content-between font-weight-bold mt-2 mk-total"><span>Estimated total</span><span>{{ money(total) }}</span></div>
            <p class="small text-muted mt-2">
              Each vendor confirms availability and the final amount. Payment is arranged with the vendor and recorded on the order.
            </p>

            <label for="cart-notes" class="small font-weight-bold">Notes for the vendor(s)</label>
            <textarea id="cart-notes" v-model="notes" rows="3" class="form-control mb-3" placeholder="Delivery address, contact person, timing…"></textarea>

            <div v-if="errors.length" class="alert alert-danger small py-2">
              <div v-for="e in errors" :key="e">{{ e }}</div>
            </div>

            <template v-if="isBuyer">
              <button type="button" class="btn mk-btn btn-block" :disabled="placing" @click="checkout">
                <span v-if="placing" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                Place order{{ groups.length > 1 ? "s" : "" }}
              </button>
            </template>
            <div v-else-if="signedIn" class="alert alert-warning small mb-0">
              Only health facility accounts can place marketplace orders.
            </div>
            <div v-else class="alert alert-info small mb-0">
              <router-link to="/">Sign in</router-link> with your health facility account to place the order.
              Your cart is kept on this device.
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import MarketplaceHeader from "./MarketplaceHeader.vue";
import cart from "./cart";

export default {
  components: { MarketplaceHeader },
  data() {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem("user") || "null");
    } catch (e) {
      user = null;
    }
    const jwt = localStorage.getItem("jwt");
    return {
      items: cart.items(),
      notes: "",
      placing: false,
      errors: [],
      signedIn: !!jwt && jwt !== "null",
      isBuyer: !!jwt && jwt !== "null" && user && (user.type === "provider" || user.type === "provider_employee"),
    };
  },
  computed: {
    groups() {
      const byVendor = {};
      this.items.forEach((item) => {
        const key = item.product.vendor.id;
        if (!byVendor[key]) byVendor[key] = { vendor: item.product.vendor, items: [], total: 0 };
        byVendor[key].items.push(item);
        byVendor[key].total += item.quantity * item.product.unit_price;
      });
      return Object.values(byVendor);
    },
    total() {
      return this.groups.reduce((sum, g) => sum + g.total, 0);
    },
  },
  mounted() {
    this._onCart = () => (this.items = cart.items());
    window.addEventListener(cart.EVENT, this._onCart);
    this.addFromLink();
  },
  beforeDestroy() {
    window.removeEventListener(cart.EVENT, this._onCart);
  },
  methods: {
    money(v) {
      return "₦" + Number(v || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 });
    },
    setQty(item, value) {
      cart.setQuantity(item.product.id, value);
    },
    remove(item) {
      cart.remove(item.product.id);
    },
    // ?add=<product id>:<quantity>,… (the assessment offers email): adds those products at those quantities.
    addFromLink() {
      const pairs = String(this.$route.query.add || "").split(",")
        .map((p) => p.split(":").map(Number))
        .filter(([id, qty]) => id > 0 && qty > 0)
        .slice(0, 50);
      if (!pairs.length) return;
      this.$router.replace({ path: this.$route.path });
      const inCart = cart.items().map((i) => i.product.id);
      Promise.all(pairs.filter(([id]) => !inCart.includes(id)).map(([id, qty]) =>
        this.axios.get(`/api/v1/marketplace/products/${id}`)
          .then((res) => { cart.add(res.data.data, qty); return true; })
          .catch(() => false)
      )).then((results) => {
        const added = results.filter(Boolean).length;
        const missing = results.length - added;
        if (added) this.$toasted.success(`${added} item(s) from your assessment added`, { position: "top-center", duration: 4000 });
        if (missing) this.$toasted.info(`${missing} item(s) are no longer available`, { position: "top-center", duration: 4000 });
      });
    },
    checkout() {
      this.placing = true;
      this.errors = [];
      const payload = {
        notes: this.notes,
        items: this.items.map((i) => ({ vendor_product_id: i.product.id, quantity: i.quantity })),
      };
      this.axios
        .post("/api/v1/auth/marketplace/orders", payload)
        .then((res) => {
          cart.removeMany(payload.items.map((i) => i.vendor_product_id));
          this.$toasted.success(res.data.message || "Order placed", { position: "top-center", duration: 4000 });
          const orders = res.data.data || [];
          this.$router.push(orders.length === 1 ? `/procurement/orders/${orders[0].id}` : "/procurement/orders");
        })
        .catch((err) => {
          const data = (err.response && err.response.data) || {};
          const itemErrors = data.errors && (data.errors.items || Object.values(data.errors).flat());
          this.errors = itemErrors && itemErrors.length ? itemErrors : [data.message || "The order could not be placed."];
        })
        .finally(() => (this.placing = false));
    },
  },
};
</script>

<style scoped>
.mk-page { background: #f1f5f9; min-height: 100vh; }
.mk-panel { background: #fff; border-radius: 12px; padding: 18px; box-shadow: 0 1px 4px rgba(0,0,0,.05); }
.mk-line { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-top: 1px solid #f1f5f9; }
.mk-line-img { width: 54px; height: 54px; border-radius: 8px; background: #f8fafc; display: flex; align-items: center; justify-content: center; color: #cbd5e1; overflow: hidden; flex-shrink: 0; }
.mk-line-img img { width: 100%; height: 100%; object-fit: cover; }
.mk-line-total { width: 110px; text-align: right; font-weight: 600; }
.mk-total { font-size: 1.1rem; color: #1e3a5f; border-top: 1px solid #e2e8f0; padding-top: 8px; }
.mk-btn { background: #1e3a5f; color: #fff; border-radius: 8px; font-weight: 600; }
.mk-btn:hover { opacity: .9; color: #fff; }
@media (max-width: 576px) {
  .mk-line { flex-wrap: wrap; }
  .mk-line-total { width: auto; }
}
</style>
